/**
 * The way in: /signin goes straight to Microsoft (backend/app/api/routes/sso.py),
 * which hands the browser back signed in. It stops on a card instead when the
 * sign-in came back refused (?error=…) or the person just signed out
 * (?signed_out=1): going to Microsoft again would sign them straight back in.
 * Without Microsoft sign-in configured, it is the password form.
 */

import { useEffect } from 'react';
import { AlertTriangle, LogOut } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useProviders } from '@/api/queries';

import { AuthCard, MicrosoftButton, PasswordSignIn } from './SignInLocal';

/** Each refusal code the callback sends back, as a sentence. */
export const SIGN_IN_ERRORS: Readonly<Record<string, string>> = {
  cancelled: 'Sign-in was cancelled.',
  expired: 'Your sign-in took too long. Try again.',
  failed: "We couldn't sign you in with Microsoft. Try again, or ask an administrator if it keeps happening.",
  deactivated: 'Your account is deactivated. Ask an administrator to reactivate it.',
  local_account: 'Your account signs in with a password, not with Microsoft. Ask an administrator.',
};

/** Leaving the SPA for Microsoft; a seam so tests can watch it. */
export const browser = {
  assign: (url: string) => window.location.assign(url),
};

/** Only a path on this site counts as where to go next (`//host` would leave it); the server checks it again. */
export function safeNext(next: string | null): string | null {
  return next?.startsWith('/') && !next.startsWith('//') ? next : null;
}

export function microsoftSignInUrl(next: string | null): string {
  const safe = safeNext(next);
  return safe ? `/api/auth/sso?next=${encodeURIComponent(safe)}` : '/api/auth/sso';
}

function Redirecting({ url }: { url: string }) {
  useEffect(() => browser.assign(url), [url]);
  return (
    <AuthCard>
      <div className="stack-md" role="status" aria-live="polite">
        <p>Redirecting to Microsoft…</p>
        <p className="hint">
          <a href={url}>Continue to Microsoft</a>
        </p>
      </div>
    </AuthCard>
  );
}

function StoppedCard({ error, url }: { error: string | null; url: string }) {
  return (
    <AuthCard>
      <div className="stack-md">
        {error ? (
          <div className="alert alert-warning" role="alert">
            <AlertTriangle aria-hidden="true" />
            <div className="a-body">{SIGN_IN_ERRORS[error] ?? SIGN_IN_ERRORS.failed}</div>
          </div>
        ) : (
          <h3 className="row">
            <LogOut aria-hidden="true" /> You&apos;re signed out
          </h3>
        )}
        <MicrosoftButton href={url} />
      </div>
    </AuthCard>
  );
}

export function SignIn() {
  const providers = useProviders();
  const [params] = useSearchParams();
  const url = microsoftSignInUrl(params.get('next'));
  const error = params.get('error');

  if (providers.isPending) {
    return (
      <AuthCard>
        <p className="hint" role="status">Loading…</p>
      </AuthCard>
    );
  }
  if (!providers.data?.entra) return <PasswordSignIn />;
  if (error || params.has('signed_out')) return <StoppedCard error={error} url={url} />;
  return <Redirecting url={url} />;
}
